import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../services/api';
import useAuth from '../hooks/useAuth';
import html2canvas from 'html2canvas';

const LEVEL_OPTIONS = [
  { value: 'campaign', label: 'Campanhas' },
  { value: 'ad_group', label: 'Grupos de Anúncios' },
  { value: 'ad', label: 'Anúncios' },
];

const OBJECTIVE_OPTIONS = [
  { value: 'ALL', label: 'Todos os Objetivos' },
  { value: 'SEARCH', label: 'Pesquisa' },
  { value: 'DISPLAY', label: 'Display' },
  { value: 'VIDEO', label: 'Vídeo' },
  { value: 'SHOPPING', label: 'Shopping' },
  { value: 'PERFORMANCE_MAX', label: 'Performance Max' },
];

const DATE_PRESETS = [
  { value: 'last_7d', label: 'Últimos 7 dias' },
  { value: 'last_14d', label: 'Últimos 14 dias' },
  { value: 'last_30d', label: 'Últimos 30 dias' },
  { value: 'current_month', label: 'Mês Atual' },
  { value: 'custom', label: 'Personalizado' },
];

const fmt = (n, dec = 2) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtCurrency = (n) => `R$ ${fmt(n)}`;
const fmtPct = (n) => `${fmt(Number(n || 0) * 100, 2)}%`;

export default function GoogleAdsReports() {
  const { user } = useAuth();
  const isAdmin = user?.roleType === 'ADMIN';
  const [connected, setConnected] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [datePreset, setDatePreset] = useState('last_30d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [level, setLevel] = useState('campaign');
  const [objective, setObjective] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [data, setData] = useState([]);
  const [shareUrl, setShareUrl] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  // Compute date range from preset
  const getDateRange = () => {
    if (datePreset === 'custom') return { startDate, endDate };
    const today = new Date();
    if (datePreset === 'current_month') {
      return {
        startDate: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    }
    const days = datePreset === 'last_7d' ? 7 : datePreset === 'last_14d' ? 14 : 30;
    const start = new Date(today);
    start.setDate(start.getDate() - days);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    };
  };

  const checkConnection = useCallback(async () => {
    try {
      const resp = await apiFetch('/google-ads/connection');
      setConnected(resp.data?.connected);
      if (resp.data?.connected) {
        const accsResp = await apiFetch('/google-ads/accounts');
        setAccounts(accsResp.data?.accounts || []);
      }
    } catch {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    checkConnection();
    // Handle OAuth redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected')) {
      checkConnection();
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('error')) {
      setError(decodeURIComponent(params.get('error')));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [checkConnection]);

  const handleConnect = async () => {
    const resp = await apiFetch('/google-ads/auth/url');
    window.location.href = resp.data?.authUrl;
  };

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      await apiFetch('/google-ads/accounts/sync', { method: 'POST' });
      const accsResp = await apiFetch('/google-ads/accounts');
      setAccounts(accsResp.data?.accounts || []);
    } catch (err) {
      setError(err.message || 'Erro ao sincronizar contas');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (selectedAccounts.length === 0) { setError('Selecione ao menos uma conta'); return; }
    setLoading(true);
    setError('');
    setData([]);
    setShareUrl('');
    try {
      const { startDate: sd, endDate: ed } = getDateRange();
      const allData = [];
      for (const customerId of selectedAccounts) {
        const queryParams = new URLSearchParams({ startDate: sd, endDate: ed, level });
        if (objective !== 'ALL') queryParams.append('objective', objective);
        const resp = await apiFetch(`/google-ads/accounts/${customerId}/insights?${queryParams.toString()}`);
        allData.push(...(resp.data || []));
      }
      setData(allData);
      setIsVisualizing(true);
    } catch (err) {
      setError(err.message || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      const { startDate: sd, endDate: ed } = getDateRange();
      const resp = await apiFetch('/google-ads/reports/share', {
        method: 'POST',
        body: JSON.stringify({
          customerIds: selectedAccounts,
          startDate: sd,
          endDate: ed,
          level,
          objective,
          name: 'Relatório Google Ads',
          snapshotData: data,
          snapshotSummary: summary,
        })
      });
      setShareUrl(resp.data?.shareUrl);
    } catch (err) {
      setError('Erro ao gerar link de compartilhamento');
    }
  };

  const handleExportPng = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const el = reportRef.current;
      const orig = { width: el.style.width, maxWidth: el.style.maxWidth };
      el.style.width = '1920px';
      el.style.maxWidth = '1920px';
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, windowWidth: 1920 });
      el.style.width = orig.width;
      el.style.maxWidth = orig.maxWidth;
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `relatorio_google_ads_${new Date().toISOString().split('T')[0]}.png`;
      link.click();
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleCopyShare = () => {
    navigator.clipboard.writeText(shareUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const toggleAccount = (id) => {
    setSelectedAccounts((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const sorted = [...data].sort((a, b) => {
    const v = (x) => Number(x[sortField] || 0);
    return sortDir === 'desc' ? v(b) - v(a) : v(a) - v(b);
  });

  const summary = data.reduce(
    (acc, r) => {
      acc.spend += r.spend; acc.impressions += r.impressions;
      acc.clicks += r.clicks; acc.conversions += r.conversions;
      return acc;
    },
    { spend: 0, impressions: 0, clicks: 0, conversions: 0 }
  );
  summary.ctr = summary.impressions ? (summary.clicks / summary.impressions) * 100 : 0;
  summary.cpc = summary.clicks ? summary.spend / summary.clicks : 0;

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="material-symbols-outlined text-[14px] opacity-30">unfold_more</span>;
    return <span className="material-symbols-outlined text-[14px] text-primary">{sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward'}</span>;
  };

  const levelHeaders = {
    campaign: [{ key: 'name', label: 'Campanha' }],
    ad_group: [{ key: 'campaignName', label: 'Campanha' }, { key: 'name', label: 'Grupo de Anúncios' }],
    ad: [{ key: 'campaignName', label: 'Campanha' }, { key: 'adGroupName', label: 'Grupo' }, { key: 'name', label: 'Anúncio' }],
  };

  return (
    <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-7xl mx-auto w-full">
      {/* Header */}
      <header className="mb-12 flex justify-between items-center">
        <div>
          <h1 className="text-4xl lg:text-5xl font-extrabold font-headline tracking-tight text-on-surface mb-2 flex items-center gap-3">
            <span className="material-symbols-outlined text-[#4285F4]" style={{ fontSize: 'inherit' }}>ads_click</span>
            Google Ads Relatórios
          </h1>
          <p className="text-secondary max-w-2xl font-body leading-relaxed text-lg">
            Analise o desempenho das suas campanhas Google Ads
          </p>
        </div>
        <div className="flex gap-4 items-center">
          {isVisualizing && (
            <button 
              onClick={() => setIsVisualizing(false)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-surface-container hover:bg-surface-container-high transition-colors text-sm font-bold text-on-surface"
            >
              <span className="material-symbols-outlined text-[18px]">edit</span>
              Editar Filtros
            </button>
          )}
          {connected && isAdmin && !isVisualizing && (
            <button onClick={handleSync} disabled={syncLoading} className="px-6 py-3 rounded-full bg-white shadow-sm border border-slate-100 font-bold text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2">
              <span className={`material-symbols-outlined ${syncLoading ? 'animate-spin' : ''} text-slate-500`}>sync</span>
              Sincronizar Contas
            </button>
          )}
        </div>
      </header>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-8 animate-fade-in">
          <span className="material-symbols-outlined text-red-500">error</span>
          <p className="text-red-700 text-sm font-bold">{error}</p>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      )}

      {/* Connection Card */}
      {!connected ? (
        <div className="bg-surface-container-lowest rounded-2xl p-16 shadow-[0_8px_32px_rgba(205,25,249,0.04)] flex flex-col items-center justify-center gap-6 animate-fade-in text-center border border-slate-100">
          <div className="w-24 h-24 rounded-full bg-[#4285F4]/10 flex items-center justify-center">
            <span className="material-symbols-outlined text-5xl text-[#4285F4]">ads_click</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold font-headline text-slate-800 mb-2">Conecte sua conta Google Ads</h2>
            <p className="text-slate-500 max-w-md mx-auto">Autorize o acesso para visualizar, gerenciar e gerar relatórios completos de suas campanhas e ativos.</p>
          </div>
          <button onClick={handleConnect} className="primary-pill-gradient text-white px-8 py-4 rounded-full font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2 mt-4 text-lg">
            <span className="material-symbols-outlined text-xl">link</span>
            Conectar com Google
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Main Form Section */}
            {!isVisualizing && (
            <div className="xl:col-span-12 bg-surface-container-lowest rounded-xl p-6 lg:p-10 shadow-[0_8px_32px_rgba(205,25,249,0.04)] animate-in slide-in-from-top-4 fade-in duration-300">
              <div className="space-y-10">
                
                {/* Filters Row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Period */}
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label">Período de Análise</label>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {DATE_PRESETS.map((d) => (
                        <button
                          key={d.value}
                          onClick={() => setDatePreset(d.value)}
                          className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${datePreset === d.value ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                    {datePreset === 'custom' && (
                      <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-surface-container-low rounded-xl animate-fade-in">
                        <div>
                          <label className="block text-xs font-bold text-secondary mb-1">Data de Início</label>
                          <input type="date" className="w-full bg-white appearance-none px-4 py-2.5 rounded-lg border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-secondary mb-1">Data de Fim</label>
                          <input type="date" className="w-full bg-white appearance-none px-4 py-2.5 rounded-lg border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Level & Objective */}
                  <div className="space-y-3 lg:col-span-2">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label mb-3">Nível de Visualização</label>
                            <div className="relative group">
                              <select 
                                className="w-full bg-surface-container-low appearance-none px-5 py-4 rounded-xl border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm cursor-pointer"
                                value={level} 
                                onChange={(e) => setLevel(e.target.value)}
                              >
                                {LEVEL_OPTIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                              </select>
                              <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">expand_more</span>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label mb-3">Objetivo da Campanha</label>
                            <div className="relative group">
                              <select 
                                className="w-full bg-surface-container-low appearance-none px-5 py-4 rounded-xl border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm cursor-pointer"
                                value={objective} 
                                onChange={(e) => setObjective(e.target.value)}
                              >
                                {OBJECTIVE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                              <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">expand_more</span>
                            </div>
                        </div>
                     </div>
                  </div>
                </div>

                {/* Accounts */}
                <div className="pt-4 border-t border-slate-100">
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#4285F4] inline-block"></span>
                      Contas Selecionadas ({selectedAccounts.length})
                    </label>
                    <div className="relative max-w-lg">
                      <div 
                        className="w-full bg-surface-container-low px-5 py-4 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm cursor-pointer flex justify-between items-center"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                      >
                        <span className="font-medium text-slate-700 truncate block">
                          {selectedAccounts.length === 0 ? 'Selecionar contas Google...' : `${selectedAccounts.length} conta(s) selecionada(s)`}
                        </span>
                        <span className="material-symbols-outlined text-slate-400 pointer-events-none">{dropdownOpen ? 'expand_less' : 'expand_more'}</span>
                      </div>
                      {dropdownOpen && (
                        <div className="absolute z-20 w-full mt-2 bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-slate-100 max-h-60 overflow-y-auto p-2 animate-fade-in custom-scrollbar">
                          {accounts.length === 0 ? (
                            <p className="text-sm text-slate-400 p-4 border border-dashed rounded-xl border-slate-200">
                              {isAdmin
                                ? 'Nenhuma conta encontrada. Verifique se possui acesso via MCC ou contas individuais.'
                                : 'Nenhuma conta atribuída a você ainda. Solicite ao administrador para atribuir uma conta.'}
                            </p>
                          ) : (
                            accounts.filter(a => !a.isManager).map(acc => {
                              const isSelected = selectedAccounts.includes(acc.customerId);
                              return (
                                <label key={acc.id} className={`flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-[#4285F4]/10' : ''}`}>
                                  <input type="checkbox" checked={isSelected} onChange={() => toggleAccount(acc.customerId)} className="w-4 h-4 rounded text-[#4285F4] border-slate-300 focus:ring-[#4285F4]" />
                                  <div className="flex flex-col truncate">
                                    <span className={`text-sm font-bold truncate ${isSelected ? 'text-[#4285F4]' : 'text-slate-700'}`}>{acc.name}</span>
                                    <span className="text-[10px] text-slate-400 font-mono">{acc.currency} • {acc.customerId}</span>
                                  </div>
                                </label>
                              )
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-8 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
                  {data.length > 0 && (
                    <div className="flex items-center gap-4 w-full md:w-auto">
                        <button onClick={handleShare} className="px-6 py-3 rounded-full bg-surface-container font-bold text-sm text-on-surface hover:bg-surface-container-high transition-colors flex items-center gap-2 w-full md:w-auto justify-center">
                          <span className="material-symbols-outlined text-sm">share</span>
                          Compartilhar Link
                        </button>
                        {shareUrl && (
                          <div className="bg-green-50 border border-green-200 rounded-full px-4 py-2 flex items-center gap-3 flex-1 md:flex-initial overflow-hidden">
                            <span className="material-symbols-outlined text-green-600 text-sm shrink-0">link</span>
                            <span className="text-green-700 text-xs truncate">{shareUrl}</span>
                            <button onClick={handleCopyShare} className="text-green-600 hover:text-green-800 text-xs font-bold whitespace-nowrap ml-2">
                              {shareCopied ? 'Copiado!' : 'Copiar'}
                            </button>
                          </div>
                        )}
                    </div>
                  )}

                  <button
                    onClick={handleGenerate}
                    disabled={loading || selectedAccounts.length === 0}
                    className="w-full md:w-auto md:ml-auto primary-pill-gradient text-white px-8 py-4 rounded-full font-headline text-lg font-extrabold shadow-[0_8px_24px_rgba(205,25,249,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {loading ? (
                      <>
                        <span className="material-symbols-outlined animate-spin">refresh</span>
                        <span>Coletando Dados...</span>
                      </>
                    ) : (
                      <>
                        <span>Gerar Relatório</span>
                        <span className="material-symbols-outlined">analytics</span>
                      </>
                    )}
                  </button>
                </div>

              </div>
            </div>
            )}
          </div>

          {/* Results */}
          {isVisualizing && data.length > 0 && (
            <div className="mt-12 space-y-8 animate-in slide-in-from-bottom-8 fade-in duration-500">
              {/* Actions toolbar */}
              <div className="flex items-center gap-3 flex-wrap">
                <button onClick={handleShare} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-surface-container hover:bg-surface-container-high transition-colors text-sm font-bold text-on-surface">
                  <span className="material-symbols-outlined text-[18px]">share</span>
                  Compartilhar Link
                </button>
                {shareUrl && (
                  <div className="bg-green-50 border border-green-200 rounded-full px-4 py-2 flex items-center gap-2 max-w-[280px] overflow-hidden">
                    <span className="material-symbols-outlined text-green-600 text-sm shrink-0">link</span>
                    <span className="text-green-700 text-xs truncate">{shareUrl}</span>
                    <button onClick={handleCopyShare} className="text-green-600 hover:text-green-800 text-xs font-bold whitespace-nowrap">
                      {shareCopied ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                )}
                <button onClick={handleExportPng} disabled={exporting} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors text-sm font-bold disabled:opacity-50">
                  <span className="material-symbols-outlined text-[18px]">{exporting ? 'hourglass_empty' : 'image'}</span>
                  {exporting ? 'Exportando...' : 'Exportar PNG'}
                </button>
              </div>
              <div ref={reportRef}>
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                {[
                  { label: 'Investimento Google', value: fmtCurrency(summary.spend), icon: 'payments', color: 'text-blue-500', bg: 'bg-blue-50' },
                  { label: 'Impressões', value: Number(summary.impressions).toLocaleString('pt-BR'), icon: 'visibility', color: 'text-indigo-500', bg: 'bg-indigo-50' },
                  { label: 'Cliques', value: Number(summary.clicks).toLocaleString('pt-BR'), icon: 'ads_click', color: 'text-violet-500', bg: 'bg-violet-50' },
                  { label: 'CTR Médio', value: `${fmt(summary.ctr)}%`, icon: 'percent', color: 'text-fuchsia-500', bg: 'bg-fuchsia-50' },
                  { label: 'CPC Médio', value: fmtCurrency(summary.cpc), icon: 'monetization_on', color: 'text-rose-500', bg: 'bg-rose-50' },
                  { label: 'Conversões', value: fmt(summary.conversions, 0), icon: 'check_circle', color: 'text-emerald-500', bg: 'bg-emerald-50' },
                ].map((kpi, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.02)] flex flex-col items-center justify-center text-center transition-transform hover:-translate-y-1">
                    <div className={`w-12 h-12 rounded-full ${kpi.bg} flex items-center justify-center mb-3`}>
                      <span className={`material-symbols-outlined ${kpi.color} text-2xl`}>{kpi.icon}</span>
                    </div>
                    <h4 className="text-2xl font-black font-headline text-slate-800 tracking-tight leading-none mb-1">{kpi.value}</h4>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest font-label mt-1">{kpi.label}</p>
                  </div>
                ))}
              </div>

              {/* Table */}
              <div className="bg-white rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.02)] border border-slate-100 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-headline text-xl font-bold text-slate-800">Detalhamento de Performance</h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50/50">
                      <tr>
                        {levelHeaders[level].map((col) => (
                          <th key={col.key} className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest font-label">{col.label}</th>
                        ))}
                        {[
                          { key: 'spend', label: 'Investimento' },
                          { key: 'impressions', label: 'Impressões' },
                          { key: 'clicks', label: 'Cliques' },
                          { key: 'ctr', label: 'CTR' },
                          { key: 'cpc', label: 'CPC' },
                          { key: 'conversions', label: 'Conversões' },
                          { key: 'costPerConversion', label: 'CPA' },
                        ].map((col) => (
                          <th
                            key={col.key}
                            className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest font-label text-right cursor-pointer hover:bg-slate-100 select-none group transition-colors"
                            onClick={() => handleSort(col.key)}
                          >
                            <div className="flex items-center justify-end gap-1">
                              {col.label} 
                              <span className={`material-symbols-outlined text-[14px] transition-opacity ${sortField === col.key ? 'text-primary opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
                                {sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward'}
                              </span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sorted.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50/80 transition-colors">
                          {levelHeaders[level].map((col) => (
                            <td key={col.key} className="py-4 px-6 font-bold text-slate-700 text-xs truncate max-w-[200px]">
                              {row[col.key] || '—'}
                            </td>
                          ))}
                          <td className="py-4 px-6 text-right font-bold text-slate-800">{fmtCurrency(row.spend)}</td>
                          <td className="py-4 px-6 text-right font-medium text-slate-600">{Number(row.impressions || 0).toLocaleString('pt-BR')}</td>
                          <td className="py-4 px-6 text-right font-medium text-slate-600">{Number(row.clicks || 0).toLocaleString('pt-BR')}</td>
                          <td className="py-4 px-6 text-right font-medium text-slate-600">{fmtPct(row.ctr)}</td>
                          <td className="py-4 px-6 text-right font-medium text-slate-600">{fmtCurrency(row.cpc)}</td>
                          <td className="py-4 px-6 text-right font-bold text-primary">{fmt(row.conversions, 0)}</td>
                          <td className="py-4 px-6 text-right font-medium text-rose-600">{row.costPerConversion ? fmtCurrency(row.costPerConversion) : '—'}</td>
                        </tr>
                      ))}
                      {sorted.length === 0 && !loading && (
                        <tr>
                          <td colSpan={levelHeaders[level].length + 7} className="py-16 text-center text-slate-400 font-medium">
                            <span className="material-symbols-outlined text-4xl mb-3 opacity-20 block">inbox</span>
                            Nenhum dado retornado para a seleção.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
